import Dialog from '@material-ui/core/Dialog'
import Typography from '@material-ui/core/Typography'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { TFunction } from 'i18next'
import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import closeMenuIcon from '../../../assets/images/close.svg'
import {
  ProfileSocialLink,
  ProfileSocialPlatformKey,
  formatUrlWithProtocol,
  isValidProfileUrl,
} from '../../../constants'
import CHRButton from '../../button'
import Input from '../../input'
import SocialLinkRow from '../socialLinkRow'
import { styles } from './styles'

const createEmptyLink = (
  platform: ProfileSocialPlatformKey = 'instagram'
): ProfileSocialLink => ({
  id: `link-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 7)}`,
  platform,
  url: '',
})

export interface ProfileModalSubmitData {
  shortBio: string
  blogLink: string
  links: ProfileSocialLink[]
}

interface ManageProfileModalProps {
  handleClose: () => void
  openStatus: boolean
  initialShortBio: string
  initialBlogLink: string
  initialLinks: ProfileSocialLink[]
  onSubmit: (data: ProfileModalSubmitData) => Promise<void>
  t: TFunction
}

const getSocialLinkValidationError = (
  url: string,
  t: TFunction
): string | null => {
  const trimmed = url.trim()
  if (!trimmed) return String(t('shoppingBoard.socialLinkUrlRequired'))
  if (!isValidProfileUrl(trimmed)) {
    return String(t('shoppingBoard.invalidProfileUrl'))
  }
  return null
}

const ManageProfileModal = ({
  handleClose,
  openStatus,
  initialShortBio,
  initialBlogLink,
  initialLinks,
  onSubmit,
  t,
}: ManageProfileModalProps) => {
  const classes = styles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [shortBio, setShortBio] = useState('')
  const [blogLink, setBlogLink] = useState('')
  const [links, setLinks] = useState<ProfileSocialLink[]>([])
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [blogLinkError, setBlogLinkError] = useState('')
  const [formError, setFormError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (openStatus) {
      setShortBio(initialShortBio || '')
      setBlogLink(initialBlogLink || '')
      setLinks(
        initialLinks.length > 0
          ? initialLinks.map(link => ({ ...link }))
          : [createEmptyLink()]
      )
      setFieldErrors({})
      setBlogLinkError('')
      setFormError('')
    }
  }, [
    openStatus,
    initialShortBio,
    initialBlogLink,
    JSON.stringify(initialLinks),
  ])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    let nextBlogLinkError = ''

    const trimmedBlog = blogLink.trim()
    if (trimmedBlog && !isValidProfileUrl(trimmedBlog)) {
      nextBlogLinkError = String(t('shoppingBoard.invalidProfileUrl'))
    }

    const filledLinks = links.filter(link => link.url.trim())
    const incompleteRows = links.filter(link => !link.url.trim())
    const isClearingAllSocialLinks =
      links.length === 1 && incompleteRows.length === 1

    if (!isClearingAllSocialLinks) {
      incompleteRows.forEach(link => {
        errors[link.id] = String(t('shoppingBoard.socialLinkUrlRequired'))
      })
    }

    filledLinks.forEach(link => {
      const linkError = getSocialLinkValidationError(link.url, t)
      if (linkError) {
        errors[link.id] = linkError
      }
    })

    setBlogLinkError(nextBlogLinkError)
    setFieldErrors(errors)

    const hasErrors =
      Boolean(nextBlogLinkError) || Object.keys(errors).length > 0

    if (hasErrors) {
      setFormError(String(t('shoppingBoard.profileFormValidationError')))
      return false
    }

    setFormError('')
    return true
  }

  const handleAddLink = () => {
    const errors: Record<string, string> = {}

    links.forEach(link => {
      if (!link.url.trim()) {
        errors[link.id] = String(t('shoppingBoard.socialLinkUrlRequired'))
        return
      }
      const linkError = getSocialLinkValidationError(link.url, t)
      if (linkError) {
        errors[link.id] = linkError
      }
    })

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setFormError(
        errors[Object.keys(errors)[0]]?.includes('valid')
          ? String(t('shoppingBoard.profileFormValidationError'))
          : String(t('shoppingBoard.completeCurrentLinkBeforeAdd'))
      )
      return
    }

    setFormError('')
    setFieldErrors({})
    setLinks(prev => [...prev, createEmptyLink()])
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    const trimmedBlog = blogLink.trim()
    const sanitizedLinks = links
      .filter(link => link.url.trim())
      .map(({ platform, url }, index) => ({
        id: `link-${platform}-${index}`,
        platform,
        url: formatUrlWithProtocol(url.trim()),
      }))

    setIsSubmitting(true)
    try {
      await onSubmit({
        shortBio: shortBio.trim(),
        blogLink: trimmedBlog ? formatUrlWithProtocol(trimmedBlog) : '',
        links: sanitizedLinks,
      })
      handleClose()
    } catch {
      setFormError(String(t('messages.somethingWentWrong')))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setFieldErrors({})
    setBlogLinkError('')
    setFormError('')
    handleClose()
  }

  const updateLink = (index: number, updated: ProfileSocialLink) => {
    setLinks(prev => prev.map((link, i) => (i === index ? updated : link)))
    if (fieldErrors[updated.id]) {
      const linkError = updated.url.trim()
        ? getSocialLinkValidationError(updated.url, t)
        : null
      setFieldErrors(prev => {
        const next = { ...prev }
        if (linkError) {
          next[updated.id] = linkError
        } else {
          delete next[updated.id]
        }
        return next
      })
    }
  }

  const handleBlogLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setBlogLink(value)
    if (blogLinkError) {
      if (!value.trim() || isValidProfileUrl(value)) {
        setBlogLinkError('')
      }
    }
  }

  const removeLink = (index: number) => {
    setLinks(prev => {
      if (prev.length === 1) return [createEmptyLink()]
      return prev.filter((_, i) => i !== index)
    })
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={openStatus}
      onClose={handleCancel}
      aria-labelledby="manageSocialLinks"
      className={classes.modal}
    >
      <section className={classes.modalContainer}>
        <button
          type="button"
          onClick={handleCancel}
          className={classes.closeButton}
        >
          <img src={closeMenuIcon} alt={t('imageAlts.close')} />
        </button>
        <Typography variant="h3" component="h3" className={classes.title}>
          {String(t('shoppingBoard.profileUpdateTitle'))}
        </Typography>

        <div className={classes.fieldGroup}>
          <label className={classes.fieldLabel} htmlFor="profile-short-bio">
            {String(t('shoppingBoard.profileBioLabel'))}
          </label>
          <textarea
            placeholder={String(t('shoppingBoard.addShortBio'))}
            value={shortBio}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setShortBio(event.target.value)
            }
            rows={4}
            className={classes.shortBioTextarea}
          />
        </div>

        <div className={classes.fieldGroup}>
          <label className={classes.fieldLabel} htmlFor="profile-blog-link">
            {String(t('shoppingBoard.profileBlogLinkLabel'))}
          </label>
          <Input
            type="text"
            placeholder={String(t('shoppingBoard.addBlogLink'))}
            value={blogLink}
            error={Boolean(blogLinkError)}
            onChange={handleBlogLinkChange}
          />
          {blogLinkError && (
            <p className={classes.fieldError}>{blogLinkError}</p>
          )}
        </div>

        <div className={classes.sectionDivider} />
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {String(t('shoppingBoard.socialLinksSectionTitle'))}
        </Typography>

        <div className={classes.socialLinksContainer}>
          {links.map((link, index) => (
            <SocialLinkRow
              key={link.id}
              link={link}
              onChange={updated => updateLink(index, updated)}
              onRemove={() => removeLink(index)}
              canRemove={links.length > 1}
              error={fieldErrors[link.id]}
            />
          ))}
        </div>

        <button
          type="button"
          className={classes.addLinkButton}
          onClick={handleAddLink}
        >
          + {String(t('shoppingBoard.addNewSocialLink'))}
        </button>

        {formError && <p className={classes.formError}>{formError}</p>}

        <div className={classes.actions}>
          <CHRButton
            label={String(t('shoppingBoard.cancel'))}
            onClick={handleCancel}
            customStyle={classes.cancelButton}
            disabled={isSubmitting}
          />
          <CHRButton
            label={String(t('shoppingBoard.submit'))}
            onClick={handleSubmit}
            hoverEffects={true}
            isSubmitting={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </section>
    </Dialog>
  )
}

export default withTranslation()(ManageProfileModal)
